import React, { useState } from "react";
import { makeStyles } from "@mui/styles";
import {
  Box,
  Chip,
  Collapse,
  Dialog,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { Theme, useTheme } from '@mui/material/styles'
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import Draggable from 'react-draggable';
import { DetailState } from "./Planning";
import { PlanningItem } from './index';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import dateFormatter from '../../utils/DateFormatter'
import NoTransferIcon from "@mui/icons-material/NoTransfer";
import { calcNewAverages } from "src/utils/GetPlanning";
import { seasonalityHeaderContent } from "src/utils/SeasonalityHelper";

interface Inputs {
  data: DetailState;
  open: any;
  onClose: any;
}

interface MatDesc {
  materialNum: string,
  materialNumShort: string,
  description: string
}

interface SettlementHistory {
  shipmentNum: string,
  route: string,
  visitDt: Date,
  meterReading: number,
  totalizerReading: number,
  lastMeterReading: number,
  lastTotalizerReading: number,
  oos: number,
  fillRate: number,
  txSettlementItems: any,
}

const useRowStyles = makeStyles({
  root: {
    "& > *": {
      borderBottom: "unset",
    },
  },
});

const useStyles = makeStyles((theme: Theme) => ({
  table: {},
  chipActual: {
    marginTop: theme.spacing(2),
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(2)
  },
  chipActual1: {
    marginRight: theme.spacing(1),
  },
  chipEFDisplay: {
    marginTop: -theme.spacing(1),
    marginRight: theme.spacing(1),
    marginBottom: -theme.spacing(1)
  }
}));

function PaperComponent(props: any) {
  return (
    <Draggable handle="#dri-title" cancel={'[class*="MuiDialogContent-root"]'}>
      <Paper {...props} />
    </Draggable>
  );
}

function ExpectedFillsDisplay(props:{planningItem:PlanningItem }){
  const classes = useStyles();

  let pi = props.planningItem;
  if(pi.expectedFills.length > 0){
    let expected_fill_dates = pi.expectedFills.map(ef=>new Date(Date.parse(`${ef.fillDt}`)).toISOString().split("T")[0]);
    return <Chip
              label={`Expected to visit ${expected_fill_dates.sort().join(', ')}`}
              color="primary"
              className={classes.chipEFDisplay}
              icon={<LocalShippingIcon/>}
              variant="filled"
            />
  }
  return (<div/>)
}

export default function Inventory(props: Inputs) {
  const classes = useStyles();
  //const [enableNewAvg, setEnableNewAvg] = useState(false);
  const [newAvgCalc, setNewAvgCalc] = useState<any>({});
  const [loaded, setLoaded] = useState<boolean>(false);

  if (!props.open || !props.data) {
    return null
  }

  const planningItem:any = props.data.planningItem;
  const matDetails = props.data.matDetails;
  const matDesc: MatDesc[] = props.data.settlementData.matDesc;
  const settlements: SettlementHistory[] = props.data.settlementData.settlements;
  const salesOrg=props.data.salesOrg;
  var isVisitCancelled = false;
  let loadDates = '';
  let msg = '';

  if(newAvgCalc&&newAvgCalc.matAvgDetails&&newAvgCalc.matAvgDetails.length>0&&newAvgCalc.matAvgDetails[0].customer_num!=planningItem?.customerNum.toString()){
    setNewAvgCalc({enableNewAvg:false,matAvgDetails:[]});
  }

  const handleNewAvgClick = () => {
    if(newAvgCalc&&(!newAvgCalc.enableNewAvg||(!newAvgCalc.matAvgDetails||newAvgCalc.matAvgDetails.length<=0))){
      let invoice_count=6;
      let wghtd_avg=2;
      if(planningItem&&planningItem!.details&&planningItem!.details.length>0&&planningItem!.details[0]){
        if(planningItem!.details[0]&&planningItem!.details[0].average_based_on_rec){
          invoice_count=planningItem!.details[0].average_based_on_rec;
        }
        if(planningItem!.details[0]&&planningItem!.details[0].appld_wght){
          wghtd_avg=planningItem!.details[0].appld_wght+1;
        }
      }
        calcNewAverages(salesOrg, planningItem?.customerNum.toString(), planningItem?.equipmentNum.toString(),invoice_count,wghtd_avg).then((v: any) => {
          if(v&&v.newavg&&v.newavg.length>0){
            setNewAvgCalc({enableNewAvg:true,matAvgDetails:v.newavg});
          }
        });
      }else{
        setNewAvgCalc({enableNewAvg:false,matAvgDetails:[]});
      }
  };

  const Row = (props: any) => {
    const { position,row,details } = props;
    const [open, setOpen] = React.useState(false);
    const classes = useRowStyles();
    var meterReading:string = "" + row.meterReading;
    var totalizerReading:string = "" + row.totalizerReading.toFixed(2);

    if (row.lastMeterReading > 0) {
      const diff = row.meterReading - row.lastMeterReading;
      meterReading = meterReading + " (+" + diff + ")";
    }
    if (row.lastTotalizerReading > 0) {
      const diff = row.totalizerReading - row.lastTotalizerReading;
      totalizerReading = totalizerReading + " (+" + diff.toFixed(2) + ")";
    }
    const sDays= details&&details.length>0&&details[0].sdays?JSON.parse(details[0].sdays):null;
    const appld_wght= details&&details.length>0?details[0].appld_wght:0;                 
    return (
      <React.Fragment>
        <TableRow className={classes.root}>
          <TableCell>
            <IconButton size="small" onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </TableCell>
          <TableCell component="th" scope="row">
            {row.visitDt}
          </TableCell>
          <TableCell align="right">{row.shipmentNum}</TableCell>
          <TableCell align="left">{meterReading}</TableCell>
          <TableCell align="left">{totalizerReading}</TableCell>
          {newAvgCalc&&Object.keys(newAvgCalc).length>0&&newAvgCalc.enableNewAvg&&newAvgCalc.matAvgDetails&&newAvgCalc.matAvgDetails.length>0 ? (<TableCell align="right">{sDays&&sDays.length>0?sDays[position]:'-'}</TableCell> ):<></>}
          <TableCell align="right">{row.oos}</TableCell>           
          <TableCell align="right">{row.fillRate}%</TableCell>           
        </TableRow>
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box margin={1}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell align="right">Material Number</TableCell>
                      <TableCell align="right">Description</TableCell>
                      {newAvgCalc&&Object.keys(newAvgCalc).length>0&&newAvgCalc.enableNewAvg&&newAvgCalc.matAvgDetails&&newAvgCalc.matAvgDetails.length>0 ? (<TableCell align="right">Sold Qty</TableCell>):<></>}
                      {newAvgCalc&&Object.keys(newAvgCalc).length>0&&newAvgCalc.enableNewAvg&&newAvgCalc.matAvgDetails&&newAvgCalc.matAvgDetails.length>0 ? (<TableCell align="right">New Sold Qty</TableCell> ):<></>} 
                      <TableCell align="right">Fill Qty</TableCell>
                      <TableCell align="right">Closing Inventory</TableCell>
                      {newAvgCalc&&Object.keys(newAvgCalc).length>0&&newAvgCalc.enableNewAvg&&newAvgCalc.matAvgDetails&&newAvgCalc.matAvgDetails.length>0 ? (<TableCell align="right">W-Vends/Day</TableCell>):<></>}
                      {newAvgCalc&&Object.keys(newAvgCalc).length>0&&newAvgCalc.enableNewAvg&&newAvgCalc.matAvgDetails&&newAvgCalc.matAvgDetails.length>0 ? (<TableCell align="right">New W-Vends/Day</TableCell>):<></>}
                      <TableCell align="right">Vend Price</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {row.txSettlementItems.map((item: any, index: number) => {
                      var desc = "";
                      const mDesc = matDesc.find(elem => elem.materialNum === item.materialNum);
                      if (mDesc) {
                        desc = mDesc.description;
                      }
                      const matDetail=details.find((detailItem: { material_num: any; }) => detailItem.material_num === item.materialNum)
                      const sold=matDetail&&matDetail.sold?JSON.parse(matDetail.sold):'-';
                      let newSold:any;
                      let newSdays:any;
                      if(newAvgCalc&&Object.keys(newAvgCalc).length>0&&newAvgCalc.matAvgDetails&&newAvgCalc.matAvgDetails?.length>0){
                        const newMatDetails=newAvgCalc?.matAvgDetails.find((detailItem: { material_num: any; }) => detailItem.material_num === item.materialNum);
                        newSold=newMatDetails&&newMatDetails.sold?JSON.parse(newMatDetails.sold):[];
                        newSdays=newMatDetails&&newMatDetails.sDays?JSON.parse(newMatDetails.sDays):[];
                      }

                      return (
                      <TableRow key={index} selected={item.quantity != 0}>
                        <TableCell component="th" scope="row" align="right">
                          {item.materialNum.replace(/^0+/, "")}
                        </TableCell>
                        <TableCell align="right">
                          {desc}
                        </TableCell>
                        {newAvgCalc&&Object.keys(newAvgCalc).length>0&&newAvgCalc.enableNewAvg&&newAvgCalc.matAvgDetails&&newAvgCalc.matAvgDetails?.length>0 ? (<TableCell align="right">
                          {sold&&sold.length>0&&sold!='-'?sold[position]+' '+item.uom:'-'}
                        </TableCell>):<></>}  
                        {newAvgCalc&&Object.keys(newAvgCalc).length>0&&newAvgCalc.enableNewAvg&&newAvgCalc.matAvgDetails&&newAvgCalc.matAvgDetails?.length>0 ? (<TableCell align="right">
                          {newSold&&newSold.length>0&&newSold!='-'?newSold[position]+' '+item.uom:'-'}
                        </TableCell> ):<></>}                        
                        <TableCell align="right">
                          {item.quantity}&nbsp;{item.uom}
                        </TableCell>
                        <TableCell align="right">
                          {item.closingInventory}
                        </TableCell>
                        {newAvgCalc&&Object.keys(newAvgCalc).length>0&&newAvgCalc.enableNewAvg&&newAvgCalc.matAvgDetails&&newAvgCalc.matAvgDetails?.length>0 ? (<TableCell align="right">
                          {sold&&sold.length>0&&sold!='-'?(sold[position]/(sDays[position]==0?1:sDays[position])).toFixed(2)+' '+item.uom:'-'}
                        </TableCell>):<></>}
                        {newAvgCalc&&Object.keys(newAvgCalc).length>0&&newAvgCalc.enableNewAvg&&newAvgCalc.matAvgDetails&&newAvgCalc.matAvgDetails?.length>0 ? (<TableCell align="right">
                          {newSold&&newSold.length>0&&newSold!='-'?(newSold[position]/(newSdays[position]==0?1:newSdays[position])).toFixed(2)+' '+item.uom:'-'}
                        </TableCell> ):<></>}
                        <TableCell align="right">
                          {item.vendPrice.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    )})}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  };

  var settlementCount = 0;
  if(planningItem?.cancelVisitDates && planningItem?.cancelVisitDates[0]?.load_date!=null){
    isVisitCancelled = true;
    loadDates = planningItem?.cancelVisitDates[0]?.load_date;
  }
  msg = "Visit cancelled: ";

  const seasonalityConent = seasonalityHeaderContent(planningItem?.seasonalityConfig[0], planningItem?.selMnthStlmts, planningItem?.settlementThreshold);  
  return (
    <Dialog
      fullWidth={true}
      maxWidth="md"
      aria-labelledby="dri-title"
      PaperComponent={PaperComponent}
      open={props.open}
      onClose={props.onClose}
    >
      <DialogTitle id="dri-title" style={{ cursor: 'move' }}>Machine Inventory Details for {planningItem!.customerNum}: {planningItem!.name} / {planningItem!.assetNum}  
      {/* {enableNewAvg ? ( */}
      <Chip
      label={'New Avg'}
      style={{ padding: "0px",marginLeft:"5px" }}
      color="primary"
      onClick={() => {handleNewAvgClick();}}/>
      {/* ):<></>}  */}
      </DialogTitle>
      <ExpectedFillsDisplay planningItem={planningItem!} />
      <Chip
      icon={<NoTransferIcon />}
      label={msg + loadDates}
      className={classes.chipActual1}
      color="primary"
      style = {{display: (!isVisitCancelled)?'none':''}}
      />
      {
        (seasonalityConent.text ?
        <div style={{textAlign:'center', fontWeight: "bold"}}><span style={{fontSize: "17px"}}>Season: </span><span style={{color: seasonalityConent.textColor, fontSize: "14px"}}>{`${seasonalityConent.text} `}</span><span style={{fontSize: "14px"}}>{seasonalityConent.nextSeasonality}</span></div>
        :<></>
        )
      }
      <Chip
        label={"Machine Inventory Prediction - last inventory update was " + dateFormatter(new Date(planningItem!.lastInventoryUpdate), 'd MMM DD YYYY hh:mm:ss') + ", sales days since: " + planningItem!.numSalesDays}
        className={classes.chipActual}
        color="primary"
      />
      <TableContainer component={Paper}>
        <Table className={classes.table} size="small">
          <TableHead>
            <TableRow>
              <TableCell>Material</TableCell>
              <TableCell align="right">Consignment</TableCell>
              <TableCell align="right">Last Closing Inventory</TableCell>
              <TableCell align="right">Last Known Inventory</TableCell>
              { seasonalityConent.isSeasonalityApplied ? <TableCell align="right">Avg Sales/Day-Season</TableCell>:<></>}
              <TableCell align="right">W-Vends/Day</TableCell>
              {newAvgCalc&&newAvgCalc.enableNewAvg&&newAvgCalc.matAvgDetails&&newAvgCalc.matAvgDetails.length>0 ? (<TableCell align="right">New W-Vends/Day</TableCell> ):<></>} 
              <TableCell align="right">Avg based on Settlements</TableCell>
              <TableCell align="right">Trend</TableCell>
              <TableCell align="right">Predicted Inventory</TableCell>
              <TableCell align="right"># Needed</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {planningItem!.details.map((row:any, index: number) => {
              const matnr = row.material_num;
              const matDetail = matDetails.find(elem => elem.materialNum === matnr);
              var matDesc = matnr.replace(/^0+/, "");
              if (matDetail) {
                matDesc = matDesc + ": " + matDetail.description;
              }
              //const prediction = Math.ceil(planningItem!.numSalesDays * row.average_consumption);
              const matPlanDetail:any=planningItem?.details.find((detailItem: { material_num: any; }) => detailItem.material_num === row.material_num);
              const averages=matPlanDetail&&matPlanDetail.average_data?JSON.parse(matPlanDetail?.average_data):[];
              let averageInfo='';
              const appld_wght:number= matPlanDetail&&matPlanDetail.appld_wght?matPlanDetail.appld_wght:0;  
              let weightInfo='';
              for (const [index, _average] of averages.entries()) {  
                if(index+1<=appld_wght){
                  if(weightInfo.length>0){
                    weightInfo=weightInfo+'+'+_average;
                  }else{
                    weightInfo=_average;
                  }
                  
                }
                averageInfo=averageInfo+'+'+_average;
                if(index+1==averages.length){
                  averageInfo='Weighted average('+appld_wght+')=('+weightInfo+averageInfo+')/'+(averages.length+appld_wght);
                }
              }
              

              let newMatDetails:any;
              let newaverageInfo='';
              if(newAvgCalc&&newAvgCalc.matAvgDetails&&newAvgCalc.matAvgDetails.length>0){
                newMatDetails=newAvgCalc?.matAvgDetails.find((detailItem: { material_num: any; }) => detailItem.material_num === row.material_num);
                const newAverageData=newMatDetails&&newMatDetails.average_data?JSON.parse(newMatDetails?.average_data):[];
                let newWeightInfoDetail:string='';
                for (const [index, _average] of newAverageData.entries()) {  
                  if(index+1<=appld_wght){
                    if(newWeightInfoDetail.length>0){
                      newWeightInfoDetail=newWeightInfoDetail+'+'+_average;
                    }else{
                      newWeightInfoDetail=_average;
                    }
                    
                  }
                  newaverageInfo=newaverageInfo+'+'+_average;
                  if(index+1==newAverageData.length){
                    newaverageInfo='Weighted average('+appld_wght+')=('+newWeightInfoDetail+newaverageInfo+')/'+(newAverageData.length+appld_wght);
                  }
                }
              }
              
              return (
                <TableRow key={index} selected={row.material_num === planningItem!.criticalMaterialNum}>
                  <TableCell component="th" scope="row">
                    {matDesc}
                  </TableCell>
                  <TableCell align="right">{row.consignment}</TableCell>
                  <TableCell align="right">{row.init_inventory}</TableCell>
                  <TableCell align="right">{row.last_inventory}</TableCell>
                  <TableCell align="right">{row.average_consumption ? row.average_consumption.toFixed(2) : "0"}</TableCell>
                  {newAvgCalc&&newAvgCalc.enableNewAvg&&newAvgCalc.matAvgDetails&&newAvgCalc.matAvgDetails.length>0 ? (<Tooltip title={newaverageInfo} placement='top'><TableCell align="right">{newMatDetails&&newMatDetails?.wght_average_consumption ? newMatDetails.wght_average_consumption.toFixed(2) : "0"}</TableCell></Tooltip> ):<></>} 
                  {seasonalityConent.isSeasonalityApplied  ? <Tooltip title={averageInfo} placement='top'><TableCell align="right">{row?.average_consumption_act.toFixed(2) || 0}</TableCell></Tooltip> :<></>}
                  <TableCell align="right">{row.average_based_on_rec}</TableCell>
                  <TableCell align="right">{row.average_trend ? row.average_trend.toFixed(2) : "0"}</TableCell>
                  <TableCell align="right">{row.current_inventory}</TableCell>
                  <TableCell align="right">{row.refill_qty}</TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Chip
        label="Last Settlements"
        className={classes.chipActual}
        color="primary"
        //onClick={() => {handleNewAvgClick();}}
      />
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Visit Date</TableCell>
              <TableCell align="right">Shipment</TableCell>
              <TableCell align="left">Meter</TableCell>
              <TableCell align="left">Totalizer</TableCell>
              {newAvgCalc&&Object.keys(newAvgCalc).length>0&&newAvgCalc.enableNewAvg&&newAvgCalc.matAvgDetails&&newAvgCalc.matAvgDetails.length>0 ? (<TableCell align="left">Sales Days</TableCell>):<></>} 
              <TableCell align="right">Materials OOS</TableCell>
              <TableCell align="right">Fill rate</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {settlements.map((item, index) => {
              settlementCount++;
              if (settlementCount > 5) {
                return null;
              }
              if ((index + 1) < settlements.length) {
                const next = settlements[index + 1];
                item.lastTotalizerReading = next.totalizerReading;
                item.lastMeterReading = next.meterReading;                
              } else {
                item.lastTotalizerReading = 0;
                item.lastMeterReading = 0;
              }
              // fill rate and out-of-stocks
              var totalFillUnits = 0;
              var totalClosingInv = 0;
              var totalOOS = 0;
              var fillRate = 0;
              item.txSettlementItems.map((sitem: any, index: number) => {
                totalFillUnits += sitem.quantity;
                totalClosingInv += sitem.closingInventory;
                if (sitem.quantity >= sitem.closingInventory) {
                  totalOOS++;
                }
              });
              if (totalClosingInv > 0) {
                fillRate = Math.ceil((totalFillUnits / totalClosingInv) * 100);
              }
              item.fillRate = fillRate;
              item.oos = totalOOS;
              return (
              <Row key={index} position={index} row={item} details={planningItem?.details} />
            )})}
          </TableBody>
        </Table>
      </TableContainer>

    </Dialog>
  );
}
